import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';
import invoiceGenerator from '../../services/invoiceGenerator';
import './Products.css';
import './ProductsActions.css';
import './MobileResponsive.css';
import apiService from '../../services/api';

const PurchaseOrder = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [perPage, setPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [openActionId, setOpenActionId] = useState(null);
  const [error, setError] = useState(null);

  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSupplierId, setFilterSupplierId] = useState('all');
  const [filterLocationId, setFilterLocationId] = useState('all');
  const [filterShippingStatus, setFilterShippingStatus] = useState('all');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: null, bottom: null, left: 0, maxHeight: null });

  const [locations, setLocations] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  useEffect(() => {
    if (!openActionId) return;
    const handleClickOutside = (e) => {
      if (e.target.closest('.action-btn-trigger') || e.target.closest('.action-dropdown-menu')) return;
      setOpenActionId(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openActionId]);

  const toggleDropdown = (rowId, event) => {
    event.stopPropagation();
    if (openActionId === rowId) {
      setOpenActionId(null);
    } else {
      const rect = event.currentTarget.getBoundingClientRect();
      const gap = 8;
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const menuMinWidth = 220;
      const edgePadding = 8;

      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      const openUpwards = spaceBelow < 240 && spaceAbove > spaceBelow;
      const availableHeight = openUpwards ? (spaceAbove - gap - edgePadding) : (spaceBelow - gap - edgePadding);
      const maxHeight = Math.max(160, Math.min(Math.floor(viewportHeight * 0.6), Math.floor(availableHeight)));

      const left = Math.min(
        Math.max(rect.left, edgePadding),
        Math.max(edgePadding, viewportWidth - menuMinWidth - edgePadding)
      );

      setDropdownPosition({
        top: openUpwards ? null : (rect.bottom + gap),
        bottom: openUpwards ? (viewportHeight - rect.top + gap) : null,
        left,
        maxHeight
      });
      setOpenActionId(rowId);
    }
  };

  useEffect(() => { fetchPurchaseOrders(); }, [currentPage, perPage]);

  useEffect(() => {
    const timer = setTimeout(() => { setCurrentPage(1); fetchPurchaseOrders(); }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        const locResp = await apiService.getLocations();
        setLocations(locResp?.data || locResp || []);
      } catch (e) { /* console.warn('Failed to fetch locations:', e); */ }
      try {
        const supResp = await apiService.getContacts();
        const contactList = supResp?.data || supResp || [];
        setSuppliers(contactList.filter(c => c.type === 'supplier' || c.type === 'both'));
      } catch (e) { /* console.warn('Failed to fetch suppliers:', e); */ }
    };
    fetchFilterData();
  }, []);

  const fetchPurchaseOrders = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = { page: currentPage, per_page: perPage, search: searchTerm };
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterSupplierId !== 'all') params.supplier_id = filterSupplierId;
      if (filterLocationId !== 'all') params.location_id = filterLocationId;
      if (filterShippingStatus !== 'all') params.shipping_status = filterShippingStatus;
      if (filterStartDate) params.start_date = filterStartDate;
      if (filterEndDate) params.end_date = filterEndDate;

      const response = await apiService.getPurchaseOrders(params);
      if (response.success) {
        setPurchaseOrders(response.data || []);
        setTotal(response.total || 0);
        setLastPage(response.last_page || 1);
      } else {
        setError(response.message || 'Failed to load purchase orders');
        setPurchaseOrders([]);
      }
    } catch (err) {
      /* console.error('Failed to load purchase orders', err); */
      setError(err.message || 'Failed to load purchase orders');
      setPurchaseOrders([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setOpenActionId(null);
    if (!window.confirm('Are you sure you want to delete this purchase order?')) return;
    try {
      const res = await apiService.deletePurchaseOrder(id);
      if (res && res.success !== false) {
        toast.success('Purchase order deleted successfully');
        fetchPurchaseOrders();
      } else {
        toast.error(res?.message || 'Failed to delete purchase order');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to delete purchase order');
    }
  };

  const handleStatusUpdate = async (id, status) => {
    setOpenActionId(null);
    try {
      const res = await apiService.updatePurchaseOrderStatus(id, status);
      if (res && res.success !== false) {
        toast.success(`Purchase order status updated to ${status}`);
        fetchPurchaseOrders();
      } else {
        toast.error(res?.message || 'Failed to update status');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to update status');
    }
  };

  const handlePrintPO = (po) => {
    setOpenActionId(null);
    try {
      invoiceGenerator.printInvoicePDF({
        invoiceNumber: po.ref_no || 'PO-' + po.id,
        invoiceDate: po.transaction_date || '',
        customerName: po.supplier_business_name || po.name || 'Supplier',
        items: (po.purchase_lines || []).map((p) => ({
          sku: p.product?.sku || '',
          name: p.product?.name || 'Product',
          quantity: parseFloat(p.quantity || 0),
          unitPrice: parseFloat(p.purchase_price || 0),
          total: parseFloat(p.quantity || 0) * parseFloat(p.purchase_price || 0),
        })),
        total: parseFloat(po.final_total || 0),
        subtotal: parseFloat(po.final_total || 0),
      });
    } catch (err) {
      printPage();
    }
  };

  const applyFilters = () => { setCurrentPage(1); fetchPurchaseOrders(); setShowFilters(false); };

  const resetFilters = () => {
    setFilterStatus('all');
    setFilterSupplierId('all');
    setFilterLocationId('all');
    setFilterShippingStatus('all');
    setFilterStartDate('');
    setFilterEndDate('');
    setSearchTerm('');
    setCurrentPage(1);
    fetchPurchaseOrders();
    setShowFilters(false);
  };

  const handleExport = (format) => {
    if (!purchaseOrders || purchaseOrders.length === 0) {
      alert('No data to export');
      setShowExportDropdown(false);
      return;
    }
    const headers = ['PO No', 'Date', 'Supplier', 'Location', 'Status', 'Shipping Status', 'Grand Total', 'Added By'];
    const rows = purchaseOrders.map(r => [
      r.ref_no || '', r.transaction_date || '', r.supplier_business_name || r.name || '', r.location_name || '',
      r.status || '', r.shipping_status || '', parseFloat(r.final_total || 0).toFixed(2), r.added_by || ''
    ]);
    const title = 'Purchase Orders'; const filename = 'purchase_orders';
    if (format === 'CSV') exportToCSV(headers, rows, filename);
    else if (format === 'Excel') exportToExcel(headers, rows, filename);
    else if (format === 'PDF') exportToPDF(title, headers, rows, filename);
    else if (format === 'Print') printPage();
    setShowExportDropdown(false);
  };

  const handlePrint = () => { printPage(); setShowExportDropdown(false); };
  const handleColumnVisibility = () => { alert('Column visibility'); setShowExportDropdown(false); };

  return (
    <div className="products-container">
      <div className="main-card">
        <div className="table-controls">
          <div className="table-controls-left">
            <div className="entries-dropdown">
              <span>Show</span>
              <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                <option value={25}>25</option><option value={50}>50</option><option value={100}>100</option>
              </select>
              <span>entries</span>
            </div>
          </div>
          <div className="table-controls-right">
            <div className="tabs-search-wrapper">
              <input type="text" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                className="tabs-search-input" />
              <button className="filter-toggle-btn" onClick={() => setShowFilters(true)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" /></svg>
                Filters
              </button>
            </div>
            <div className="export-dropdown-container" style={{ position: 'relative' }}>
              <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                Export ▾
              </button>
              {showExportDropdown && (
                <div className="export-dropdown-menu">
                  <button onClick={() => handleExport('CSV')}><i className="fa fa-file-csv"></i> Export CSV</button>
                  <button onClick={() => handleExport('Excel')}><i className="fa fa-file-excel"></i> Export Excel</button>
                  <button onClick={() => handleExport('Print')}><i className="fa fa-print"></i> Print</button>
                  <button onClick={() => handleExport('PDF')}><i className="fa fa-file-pdf"></i> Export PDF</button>
                </div>
              )}
            </div>
            <button className="control-btn-primary" onClick={() => navigate('/purchases/order/add')}>
              <span className="plus-icon">+</span> Add
            </button>
          </div>
        </div>

        {error && <div className="error-banner" style={{ padding: '1rem', color: '#ef4444' }}>{error}</div>}

        {isLoading ? (
          <div className="loading-sales">
            <h3>⏳ Loading Purchase Orders...</h3>
            <p>Fetching purchase orders data from server...</p>
          </div>
        ) : purchaseOrders.length === 0 ? (
          <div className="no-sales">
            <h3>📊 No Purchase Orders Found</h3>
            <p>No purchase orders have been recorded yet.</p>
          </div>
        ) : (
          <div className="products-table-container">
            <table className="products-table">
              <thead>
                <tr>
                  <th>Action</th>
                  <th>Date</th>
                  <th>PO No</th>
                  <th>Supplier</th>
                  <th>Location</th>
                  <th>Status</th>
                  <th>Shipping Status</th>
                  <th>Grand Total</th>
                  <th>Added By</th>
                </tr>
              </thead>
              <tbody>
                {purchaseOrders.map(po => (
                  <tr key={po.id}>
                        <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                          <div className="action-dropdown-container">
                            <button className="action-btn-trigger" onClick={(e) => toggleDropdown(po.id, e)}>
                              Actions <span className="arrow-down">▼</span>
                            </button>
                            {openActionId === po.id && ReactDOM.createPortal(
                              <div
                                className="action-dropdown-menu"
                                style={{
                                  position: 'fixed',
                                  top: dropdownPosition.top === null ? 'auto' : `${dropdownPosition.top}px`,
                                  bottom: dropdownPosition.bottom === null ? 'auto' : `${dropdownPosition.bottom}px`,
                                  left: `${dropdownPosition.left}px`,
                                  maxHeight: dropdownPosition.maxHeight === null ? undefined : `${dropdownPosition.maxHeight}px`,
                                  zIndex: 9999999
                                }}
                              >
                                <button className="dropdown-item" onClick={() => { setOpenActionId(null); navigate(`/purchases/order/${po.id}`); }}>👁️ View</button>
                                <button className="dropdown-item" onClick={() => handlePrintPO(po)}>🖨️ Print</button>
                                <button className="dropdown-item" onClick={() => { setOpenActionId(null); navigate(`/purchases/order/${po.id}/edit`); }}>✏️ Edit</button>
                                <button className="dropdown-item" onClick={() => handleStatusUpdate(po.id, po.status === 'ordered' ? 'received' : 'ordered')}>🔄 Toggle Status</button>
                                <button className="dropdown-item delete-item" onClick={() => handleDelete(po.id)}>🗑️ Delete</button>
                              </div>,
                              document.body
                            )}
                          </div>
                        </td>
                    <td>{po.transaction_date || 'N/A'}</td>
                    <td>{po.ref_no || 'N/A'}</td>
                    <td>{po.supplier_business_name || po.name || 'N/A'}</td>
                    <td>{po.location_name || 'N/A'}</td>
                    <td><span className={`status-badge ${po.status?.toLowerCase()}`}>{po.status || 'N/A'}</span></td>
                    <td><span className={`status-badge ${po.shipping_status?.toLowerCase()}`}>{po.shipping_status || 'N/A'}</span></td>
                    <td>${parseFloat(po.final_total || 0).toFixed(2)}</td>
                    <td>{po.added_by || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderTop: '1px solid #e2e8f0', background: 'white' }}>
              <div>
                Showing {total === 0 ? 0 : ((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, total)} of {total} entries
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <button
                  className="pagination-btn"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                >
                  Previous
                </button>
                <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem' }}>
                  Page {currentPage} of {lastPage}
                </span>
                <button
                  className="pagination-btn"
                  onClick={() => setCurrentPage(p => Math.min(lastPage, p + 1))}
                  disabled={currentPage >= lastPage}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {showFilters && (
        <div className="modal-overlay" onClick={() => setShowFilters(false)}>
          <div className="modal-container filter-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', width: '95%' }}>
            <div className="modal-header">
              <h2><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '0.5rem', verticalAlign: 'middle' }}><path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" /></svg>Filters</h2>
              <button className="modal-close-btn" onClick={() => setShowFilters(false)}>✕</button>
            </div>
            <div className="modal-content" style={{ padding: '1.5rem' }}>
              <div className="filter-row">
                <div className="filter-group">
                  <label>Status:</label>
                  <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                    <option value="all">All</option>
                    <option value="pending">Pending</option>
                    <option value="ordered">Ordered</option>
                    <option value="received">Received</option>
                    <option value="partial">Partial</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>
                <div className="filter-group">
                  <label>Shipping Status:</label>
                  <select value={filterShippingStatus} onChange={(e) => setFilterShippingStatus(e.target.value)}>
                    <option value="all">All</option>
                    <option value="not_shipped">Not Shipped</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="returned">Returned</option>
                  </select>
                </div>
                <div className="filter-group">
                  <label>Supplier:</label>
                  <select value={filterSupplierId} onChange={(e) => setFilterSupplierId(e.target.value)}>
                    <option value="all">All</option>
                    {suppliers.map(s => (
                      <option key={s.id} value={s.id}>{s.name || s.supplier_business_name || `ID: ${s.id}`}</option>
                    ))}
                  </select>
                </div>
                <div className="filter-group">
                  <label>Location:</label>
                  <select value={filterLocationId} onChange={(e) => setFilterLocationId(e.target.value)}>
                    <option value="all">All</option>
                    {locations.map(l => (
                      <option key={l.id} value={l.id}>{l.name || `ID: ${l.id}`}</option>
                    ))}
                  </select>
                </div>
                <div className="filter-group">
                  <label>Start Date:</label>
                  <input type="date" value={filterStartDate} onChange={(e) => setFilterStartDate(e.target.value)} />
                </div>
                <div className="filter-group">
                  <label>End Date:</label>
                  <input type="date" value={filterEndDate} onChange={(e) => setFilterEndDate(e.target.value)} />
                </div>
              </div>
              <div className="filter-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1rem' }}>
                <button className="control-btn-secondary" onClick={resetFilters}>Reset</button>
                <button className="control-btn-primary" onClick={applyFilters}>Apply Filters</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrder;
